import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Save } from 'lucide-react';
import { apiFetch } from '../../../../../shared/apiClient';
import { CustomSelect } from '../../../../../shared/components/CustomSelect';
import { CapacitySettingsApi } from '../../../../../features/capacity/capacitySettingsApi';
import type {
  CapacityOverride,
  CapacitySettings,
} from '../../../../../features/capacity/capacitySettingsApi';

interface ProductInfo {
  id: string;
  name: string;
}

const HARDCODED_DEFAULTS: CapacitySettings = {
  healthyLow: 65,
  healthyHigh: 90,
  overloadMax: 140,
  overloadPenaltyMax: 50,
};

const PILLAR_MAX = 15;
const SLIDER_MAX = 200;

function computeScore(utilPct: number, s: CapacitySettings): number {
  const healthyLow = s.healthyLow / 100;
  const healthyHigh = s.healthyHigh / 100;
  const overloadMax = s.overloadMax / 100;
  const overloadPenaltyMax = s.overloadPenaltyMax / 100;
  const util = utilPct / 100;

  let sCapacity: number;
  if (util >= healthyLow && util <= healthyHigh) {
    sCapacity = 1.0;
  } else if (util < healthyLow) {
    sCapacity = Math.max(0, util / healthyLow);
  } else {
    const overRange = overloadMax - healthyHigh;
    const overFraction = overRange > 0 ? Math.min(1, (util - healthyHigh) / overRange) : 1;
    sCapacity = Math.max(0, 1 - overFraction * overloadPenaltyMax);
  }
  return Math.round(PILLAR_MAX * sCapacity);
}

// ── Draggable Range Slider ────────────────────────────────────────────────────

interface RangeSliderProps {
  healthyLow: number;
  healthyHigh: number;
  overloadMax: number;
  onChange: (healthyLow: number, healthyHigh: number, overloadMax: number) => void;
}

const ZoneSlider: React.FC<RangeSliderProps> = ({
  healthyLow,
  healthyHigh,
  overloadMax,
  onChange,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<'low' | 'high' | 'max' | null>(null);
  const [dragValue, setDragValue] = useState<number | null>(null);

  const pct = (v: number) => (v / SLIDER_MAX) * 100;

  const getValueFromPointer = useCallback((clientX: number): number => {
    if (!trackRef.current) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(ratio * SLIDER_MAX);
  }, []);

  const constrainedValue = useCallback(
    (handle: 'low' | 'high' | 'max', raw: number): number => {
      const MIN_GAP = 1;
      if (handle === 'low') {
        return Math.max(1, Math.min(raw, healthyHigh - MIN_GAP));
      }
      if (handle === 'high') {
        return Math.max(healthyLow + MIN_GAP, Math.min(raw, overloadMax - MIN_GAP));
      }
      return Math.max(healthyHigh + MIN_GAP, Math.min(raw, SLIDER_MAX));
    },
    [healthyLow, healthyHigh, overloadMax]
  );

  const handlePointerDown = useCallback(
    (handle: 'low' | 'high' | 'max') => (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(handle);
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const raw = getValueFromPointer(clientX);
      setDragValue(constrainedValue(handle, raw));
    },
    [getValueFromPointer, constrainedValue]
  );

  useEffect(() => {
    if (!dragging) return;

    const handleMove = (clientX: number) => {
      const raw = getValueFromPointer(clientX);
      const v = constrainedValue(dragging, raw);
      setDragValue(v);
    };

    const handleEnd = () => {
      if (dragValue !== null) {
        if (dragging === 'low') onChange(dragValue, healthyHigh, overloadMax);
        else if (dragging === 'high') onChange(healthyLow, dragValue, overloadMax);
        else onChange(healthyLow, healthyHigh, dragValue);
      }
      setDragging(null);
      setDragValue(null);
    };

    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) handleMove(e.touches[0].clientX);
    };
    const onMouseUp = () => handleEnd();
    const onTouchEnd = () => handleEnd();

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [
    dragging,
    dragValue,
    healthyLow,
    healthyHigh,
    overloadMax,
    onChange,
    getValueFromPointer,
    constrainedValue,
  ]);

  const displayLow = dragValue !== null && dragging === 'low' ? dragValue : healthyLow;
  const displayHigh = dragValue !== null && dragging === 'high' ? dragValue : healthyHigh;
  const displayMax = dragValue !== null && dragging === 'max' ? dragValue : overloadMax;

  const zones = [
    { left: 0, width: pct(displayLow), color: 'rgba(248, 113, 113, 0.6)' },
    {
      left: pct(displayLow),
      width: pct(displayHigh) - pct(displayLow),
      color: 'rgba(74, 222, 128, 0.7)',
    },
    {
      left: pct(displayHigh),
      width: pct(displayMax) - pct(displayHigh),
      color: 'rgba(251, 191, 36, 0.6)',
    },
    { left: pct(displayMax), width: 100 - pct(displayMax), color: 'rgba(163, 163, 163, 0.4)' },
  ];

  const handles = [
    { key: 'low' as const, value: displayLow, left: pct(displayLow) },
    { key: 'high' as const, value: displayHigh, left: pct(displayHigh) },
    { key: 'max' as const, value: displayMax, left: pct(displayMax) },
  ];

  return (
    <div className="capacity-range-slider">
      <div className="capacity-range-slider__track" ref={trackRef}>
        {zones.map((z, i) => (
          <div
            key={i}
            className="capacity-range-slider__zone"
            style={{
              left: `${z.left}%`,
              width: `${z.width}%`,
              background: z.color,
            }}
          />
        ))}
        {handles.map((h) => (
          <div
            key={h.key}
            className={`capacity-range-slider__handle${dragging === h.key ? ' capacity-range-slider__handle--active' : ''}`}
            style={{ left: `${h.left}%` }}
            onMouseDown={handlePointerDown(h.key)}
            onTouchStart={handlePointerDown(h.key)}
          >
            <div className="capacity-range-slider__handle-grip" />
            <div className="capacity-range-slider__tooltip">{h.value}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main Panel Component ──────────────────────────────────────────────────────

export const CapacityScoringPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [products, setProducts] = useState<ProductInfo[]>([]);
  const [orgDefaults, setOrgDefaults] = useState<CapacitySettings>({ ...HARDCODED_DEFAULTS });
  const [overrides, setOverrides] = useState<CapacityOverride[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  const [localSettings, setLocalSettings] = useState<CapacitySettings>({ ...HARDCODED_DEFAULTS });

  const currentSaved = useMemo(() => {
    if (!selectedProductId) return orgDefaults;
    const o = overrides.find((ov) => ov.productId === selectedProductId);
    return o?.settings ?? orgDefaults;
  }, [selectedProductId, orgDefaults, overrides]);

  const isOverride = useMemo(
    () => !!selectedProductId && overrides.some((o) => o.productId === selectedProductId),
    [selectedProductId, overrides]
  );

  const hasChanges = useMemo(
    () =>
      localSettings.healthyLow !== currentSaved.healthyLow ||
      localSettings.healthyHigh !== currentSaved.healthyHigh ||
      localSettings.overloadMax !== currentSaved.overloadMax ||
      localSettings.overloadPenaltyMax !== currentSaved.overloadPenaltyMax,
    [localSettings, currentSaved]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [orgRes, overridesRes, productsRes] = await Promise.all([
          CapacitySettingsApi.getOrgCapacitySettings(),
          CapacitySettingsApi.getCapacityOverrides(),
          apiFetch('/api/products'),
        ]);
        if (cancelled) return;
        setOrgDefaults(orgRes.settings);
        setOverrides(overridesRes.overrides);
        if (productsRes.ok) {
          const payload = await productsRes.json();
          setProducts(
            (payload.products ?? []).map((p: ProductInfo) => ({ id: p.id, name: p.name }))
          );
        }
        setLocalSettings(orgRes.settings);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load capacity settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const switchProduct = useCallback(
    (productId: string | null) => {
      setSelectedProductId(productId);
      setError(null);
      setSaved(false);
      if (!productId) {
        setLocalSettings(orgDefaults);
      } else {
        const o = overrides.find((ov) => ov.productId === productId);
        const s = o?.settings ?? orgDefaults;
        setLocalSettings(s);
      }
    },
    [orgDefaults, overrides]
  );

  const handleSliderChange = useCallback(
    (healthyLow: number, healthyHigh: number, overloadMax: number) => {
      setLocalSettings((prev) => ({ ...prev, healthyLow, healthyHigh, overloadMax }));
    },
    []
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      if (!selectedProductId) {
        const res = await CapacitySettingsApi.saveOrgCapacitySettings(localSettings);
        setOrgDefaults(res.settings);
      } else {
        await CapacitySettingsApi.saveCapacityOverride(selectedProductId, localSettings);
        const updated = await CapacitySettingsApi.getCapacityOverrides();
        setOverrides(updated.overrides);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await queryClient.invalidateQueries({ queryKey: ['capacity-settings-effective'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save capacity settings');
    } finally {
      setSaving(false);
    }
  }, [selectedProductId, localSettings, queryClient]);

  const handleReset = useCallback(async () => {
    if (!selectedProductId) return;
    setSaving(true);
    setError(null);
    try {
      await CapacitySettingsApi.deleteCapacityOverride(selectedProductId);
      const updated = await CapacitySettingsApi.getCapacityOverrides();
      setOverrides(updated.overrides);
      setLocalSettings(orgDefaults);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await queryClient.invalidateQueries({ queryKey: ['capacity-settings-effective'] });
    } catch {
      setError('Failed to reset capacity override');
    } finally {
      setSaving(false);
    }
  }, [selectedProductId, orgDefaults, queryClient]);

  const handleResetToDefaults = useCallback(() => {
    setLocalSettings({ ...HARDCODED_DEFAULTS });
  }, []);

  // ── Live example calculations ──────────────────────────────────────

  const examples = useMemo(() => {
    const s = localSettings;
    return [
      { util: 50, label: 'Under-utilized' },
      { util: 80, label: 'Healthy' },
      { util: 120, label: 'Overloaded' },
      { util: 160, label: 'Max penalty' },
    ].map((ex) => {
      const score = computeScore(ex.util, s);
      return { ...ex, score };
    });
  }, [localSettings]);

  if (loading) {
    return (
      <div className="settings-panel">
        <div style={{ padding: '8px 0', color: 'var(--color-text-secondary)', fontSize: '13px' }}>
          Loading capacity settings...
        </div>
      </div>
    );
  }

  return (
    <div className="settings-panel">
      <div className="settings-panel__header">
        <h3 className="settings-panel__title">Capacity Scoring Thresholds</h3>
        <p className="settings-panel__description">
          Define how team utilization translates into the Capacity pillar score. Teams in the
          healthy range receive full points; deviations are penalized.
        </p>
      </div>

      {error && (
        <div className="settings-callout settings-callout--danger" style={{ marginBottom: '16px' }}>
          <div className="settings-callout__body">{error}</div>
        </div>
      )}

      {/* Product selector */}
      <div style={{ marginTop: '12px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px' }}>
          <span style={{ fontWeight: 500 }}>Editing settings for</span>
          <CustomSelect
            value={selectedProductId ?? ''}
            options={[
              { value: '', label: 'Organization default' },
              ...products.map((p) => ({
                value: p.id,
                label: `${p.name}${overrides.some((o) => o.productId === p.id) ? ' *' : ''}`,
              })),
            ]}
            onChange={(val) => switchProduct(val || null)}
            style={{ minWidth: '180px', fontSize: '14px' }}
          />
        </label>
        {selectedProductId && !isOverride && (
          <div
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
            }}
          >
            Using org defaults. Edit and save to create a product-specific override.
          </div>
        )}
        {isOverride && (
          <div
            style={{
              marginTop: '6px',
              fontSize: '12px',
              color: 'var(--color-text-secondary)',
            }}
          >
            Product-specific override active.{' '}
            <span style={{ color: 'var(--color-text-muted)' }}>
              Org defaults: {orgDefaults.healthyLow}–{orgDefaults.healthyHigh}% healthy,{' '}
              {orgDefaults.overloadMax}% max, {orgDefaults.overloadPenaltyMax}% penalty
            </span>
          </div>
        )}
      </div>

      {/* ── Utilization Zones ──────────────────────────────────────── */}
      <div style={{ marginTop: '8px' }}>
        <div className="settings-panel__header" style={{ marginBottom: '12px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0, color: 'var(--color-text)' }}>
            Utilization Zones
          </h4>
        </div>

        <ZoneSlider
          healthyLow={localSettings.healthyLow}
          healthyHigh={localSettings.healthyHigh}
          overloadMax={localSettings.overloadMax}
          onChange={handleSliderChange}
        />

        {/* Zone labels below track */}
        <div className="capacity-range-slider__labels">
          <span style={{ left: '0%' }}>0%</span>
          <span
            style={{
              left: `${(localSettings.healthyLow / SLIDER_MAX) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          >
            {localSettings.healthyLow}%
          </span>
          <span
            style={{
              left: `${(localSettings.healthyHigh / SLIDER_MAX) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          >
            {localSettings.healthyHigh}%
          </span>
          <span
            style={{
              left: `${(localSettings.overloadMax / SLIDER_MAX) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          >
            {localSettings.overloadMax}%
          </span>
          <span style={{ right: '0%' }}>200%</span>
        </div>

        {/* Zone details table */}
        <div className="capacity-zone-details" style={{ marginTop: '16px' }}>
          <div className="capacity-zone-details__grid">
            <div
              className="capacity-zone-details__cell"
              style={{ borderTopColor: 'rgba(248, 113, 113, 0.6)' }}
            >
              <div className="capacity-zone-details__name">Under-utilized</div>
              <div className="capacity-zone-details__range">0–{localSettings.healthyLow}%</div>
              <div className="capacity-zone-details__score">0 → {PILLAR_MAX} pts</div>
              <div className="capacity-zone-details__desc">Score ramps linearly to full</div>
            </div>
            <div
              className="capacity-zone-details__cell"
              style={{ borderTopColor: 'rgba(74, 222, 128, 0.7)' }}
            >
              <div className="capacity-zone-details__name">Healthy</div>
              <div className="capacity-zone-details__range">
                {localSettings.healthyLow}–{localSettings.healthyHigh}%
              </div>
              <div className="capacity-zone-details__score">
                {PILLAR_MAX}/{PILLAR_MAX} pts
              </div>
              <div className="capacity-zone-details__desc">Full points</div>
            </div>
            <div
              className="capacity-zone-details__cell"
              style={{ borderTopColor: 'rgba(251, 191, 36, 0.6)' }}
            >
              <div className="capacity-zone-details__name">Overloaded</div>
              <div className="capacity-zone-details__range">
                {localSettings.healthyHigh}–{localSettings.overloadMax}%
              </div>
              <div className="capacity-zone-details__score">
                {PILLAR_MAX} →{' '}
                {Math.round(PILLAR_MAX * (1 - localSettings.overloadPenaltyMax / 100))} pts
              </div>
              <div className="capacity-zone-details__desc">Score decays</div>
            </div>
            <div
              className="capacity-zone-details__cell"
              style={{ borderTopColor: 'rgba(163, 163, 163, 0.4)' }}
            >
              <div className="capacity-zone-details__name">Max Penalty</div>
              <div className="capacity-zone-details__range">{localSettings.overloadMax}–200%</div>
              <div className="capacity-zone-details__score">
                ~{Math.round(PILLAR_MAX * (1 - localSettings.overloadPenaltyMax / 100))}/
                {PILLAR_MAX} pts
              </div>
              <div className="capacity-zone-details__desc">Fixed low score</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Overload penalty ──────────────────────────────────────── */}
      <div style={{ marginTop: '20px' }}>
        <div className="settings-panel__header" style={{ marginBottom: '8px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: 600, margin: 0, color: 'var(--color-text)' }}>
            Overload penalty
          </h4>
        </div>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '14px',
          }}
        >
          <span style={{ fontWeight: 500 }}>Max penalty at ceiling</span>
          <input
            type="number"
            min={1}
            max={99}
            value={localSettings.overloadPenaltyMax}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (!Number.isNaN(v))
                setLocalSettings((prev) => ({
                  ...prev,
                  overloadPenaltyMax: Math.max(1, Math.min(99, v)),
                }));
            }}
            style={{
              width: '72px',
              padding: '6px 10px',
              borderRadius: '8px',
              border: '1px solid var(--glass-border)',
              background: 'var(--glass-bg)',
              fontSize: '14px',
            }}
          />
          <span style={{ color: 'var(--color-text-secondary)' }}>%</span>
        </label>
        <div
          style={{
            marginTop: '4px',
            fontSize: '12px',
            color: 'var(--color-text-secondary)',
          }}
        >
          At {localSettings.overloadMax}% utilization, score drops by this % of full points.
        </div>
      </div>

      {/* ── How scoring works ──────────────────────────────────────── */}
      <div
        className="settings-callout"
        style={{
          marginTop: '20px',
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: '10px',
          padding: '14px 16px',
          fontSize: '13px',
          lineHeight: 1.6,
          color: 'var(--color-text-secondary)',
        }}
      >
        <div style={{ fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '8px' }}>
          How scoring works
        </div>
        <div style={{ marginBottom: '4px' }}>Live examples with current values:</div>
        <ul style={{ margin: '4px 0 0 20px', padding: 0, listStyle: 'disc' }}>
          {examples.map((ex) => (
            <li key={ex.util}>
              Util {ex.util}% → {ex.label} → {ex.score}/{PILLAR_MAX} pts
              {ex.score >= PILLAR_MAX ? ' ✅' : ex.score <= PILLAR_MAX * 0.5 ? ' 🔶' : ' ⚠️'}
            </li>
          ))}
        </ul>
      </div>

      {/* ── Actions ─────────────────────────────────────────────────── */}
      <div
        style={{
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '8px',
        }}
      >
        {!selectedProductId && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving || !hasChanges}
            onClick={handleResetToDefaults}
            title="Reset to hardcoded defaults"
          >
            <RotateCcw size={13} />
            Reset to defaults
          </button>
        )}
        {isOverride && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving}
            onClick={handleReset}
            title="Reset to org defaults"
          >
            <RotateCcw size={13} />
            {saving ? 'Resetting…' : 'Reset to org default'}
          </button>
        )}
        {saved && <span style={{ fontSize: '0.82rem', color: 'var(--color-success)' }}>Saved</span>}
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!hasChanges || saving}
          onClick={handleSave}
        >
          <Save size={14} />
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
};
